import React, { useState, useEffect } from 'react';
import './App.css';
import Video from './models/Video';
import Question from './models/Question';
import BlogPost from './models/BlogPost';
import ArchTemplate from './models/ArchTemplate';
import { Course } from './models/Course';
import { ApiService, subscribeToNewsletter, SearchResult, SearchResponse } from './services/api';
import QuestionSubmissionForm from './components/QuestionSubmissionForm';
import BlogImageExample from './components/BlogImageExample';
import CreateBlogPost from './components/CreateBlogPost';
import ManagePosts from './components/ManagePosts';
import BlogPostDetail from './components/BlogPostDetail';
import TemplateRequestModal from './components/TemplateRequestModal';
import CreateTemplateModal from './components/CreateTemplateModal';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import UserMenu from './components/UserMenu';


const AppContent: React.FC = () => {
  const { isAuthenticated, isAdmin, userEmail } = useAuth(); // Get authentication and admin status
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [showSearchResults, setShowSearchResults] = useState<boolean>(false);
  const [searchSuggestions, setSearchSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [videos, setVideos] = useState<Video[]>([]);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showQuestionForm, setShowQuestionForm] = useState<boolean>(false);
  const [newsletterEmail, setNewsletterEmail] = useState<string>('');
  const [newsletterLoading, setNewsletterLoading] = useState<boolean>(false);
  const [newsletterMessage, setNewsletterMessage] = useState<string>('');
  const [newsletterSuccess, setNewsletterSuccess] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<string>('home');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [blogLoading, setBlogLoading] = useState<boolean>(false);
  const [blogFetched, setBlogFetched] = useState<boolean>(false);
  const [selectedBlogPostId, setSelectedBlogPostId] = useState<string | null>(null);
  const [archTemplates, setArchTemplates] = useState<ArchTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState<boolean>(false);
  const [templatesFetched, setTemplatesFetched] = useState<boolean>(false);
  const [showTemplateModal, setShowTemplateModal] = useState<boolean>(false);
  const [selectedTemplate, setSelectedTemplate] = useState<ArchTemplate | null>(null);
  const [showCreateTemplateModal, setShowCreateTemplateModal] = useState<boolean>(false);
  const [courses, setCourses] = useState<Course[]>([]);
  const [coursesLoading, setCoursesLoading] = useState<boolean>(false);
  const [coursesFetched, setCoursesFetched] = useState<boolean>(false);

  // Fetch videos, questions, and blog posts from API on component mount
  useEffect(() => {
    fetchVideos();
    fetchQuestions();
    if (!blogFetched) {
      fetchBlogPosts();
    }
    if (!templatesFetched) {
      fetchArchTemplates();
    }
    if (!coursesFetched) {
      fetchCourses();
    }
  }, [blogFetched, templatesFetched, coursesFetched]);

  const handleAskQuestionClick = () => {
    // Close mobile menu if open
    setIsMobileMenuOpen(false);
    
    // If not on home page, go to home page first
    if (currentPage !== 'home') {
      setCurrentPage('home');
      // Use setTimeout to ensure the page renders before scrolling
      setTimeout(() => {
        const askSection = document.getElementById('ask');
        if (askSection) {
          askSection.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      // Already on home page, just scroll to ask section
      const askSection = document.getElementById('ask');
      if (askSection) {
        askSection.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handlePageChange = (page: string) => {
    console.log('App: handlePageChange called with page:', page);
    setCurrentPage(page);
    setIsMobileMenuOpen(false); // Close mobile menu when navigating
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const fetchVideos = async () => {
    try {
      setLoading(true);
      setError(null);
      const popularVideos = await ApiService.getPopularVideos(10);
      setVideos(popularVideos);
    } catch (err) {
      //setError('Failed to load videos. Please try again later.');
      console.error('Failed to fetch videos:', err);
      // Fallback to empty array if API fails
      setVideos([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchQuestions = async (): Promise<void> => {
    try {
      const popularQuestions = await ApiService.getPopularQuestions(4);
      setQuestions(popularQuestions);
    } catch (error) {
      console.error('Failed to fetch questions:', error);
      // Fallback to empty array if API fails
      setQuestions([]);
    }
  };

  const fetchBlogPosts = async (): Promise<void> => {
    try {
      console.log('Fetching blog posts...');
      setBlogLoading(true);
      const featuredPosts = await ApiService.getFeaturedBlogPosts(6);
      console.log('Received blog posts:', featuredPosts);
      setBlogPosts(featuredPosts);
      setBlogFetched(true);
    } catch (error) {
      console.error('Failed to fetch blog posts:', error);
      // Fallback to empty array if API fails
      setBlogPosts([]);
      setBlogFetched(true);
    } finally {
      setBlogLoading(false);
    }
  };

  const fetchArchTemplates = async (): Promise<void> => {
    try {
      console.log('Fetching architecture templates...');
      setTemplatesLoading(true);
      const templates = await ApiService.getArchTemplates();
      console.log('Received architecture templates:', templates);
      setArchTemplates(templates);
      setTemplatesFetched(true);
    } catch (error) {
      console.error('Failed to fetch architecture templates:', error);
      // Fallback to empty array if API fails
      setArchTemplates([]);
      setTemplatesFetched(true);
    } finally {
      setTemplatesLoading(false);
    }
  };

  const handleTemplateCreated = (): void => {
    // Refresh the templates list
    setTemplatesFetched(false);
    fetchArchTemplates();
  };

  const fetchCourses = async (): Promise<void> => {
    try {
      console.log('Fetching courses...');
      setCoursesLoading(true);
      const coursesData = await ApiService.getCourses();
      console.log('Received courses:', coursesData);
      setCourses(coursesData);
      setCoursesFetched(true);
    } catch (error) {
      console.error('Failed to fetch courses:', error);
      // Fallback to empty array if API fails
      setCourses([]);
      setCoursesFetched(true);
    } finally {
      setCoursesLoading(false);
    }
  };

  const handleTemplateRequest = async (templateId: string, email: string): Promise<boolean> => {
    try {
      const result = await ApiService.requestArchTemplate(templateId, email);
      if (result.success) {
        alert(result.message);
        return true;
      } else {
        alert(`Error: ${result.message}`);
        return false;
      }
    } catch (error) {
      console.error('Failed to request template:', error);
      alert('Failed to request template. Please try again later.');
      return false;
    }
  };

  const handleTemplateButtonClick = (template: ArchTemplate) => {
    if (template.isAvailable) {
      setSelectedTemplate(template);
      setShowTemplateModal(true);
    }
  };

  // Remove hardcoded questions - now fetched from API

  const handleSearch = async (): Promise<void> => {
    if (!searchQuery.trim()) return;
    
    setIsSearching(true);
    setShowSuggestions(false);
    
    try {
      const response: SearchResponse = await ApiService.search(searchQuery, 20);
      setSearchResults(response.results);
      setShowSearchResults(true);
      console.log(`Found ${response.totalCount} results for "${searchQuery}" in ${response.searchDuration}`);
    } catch (error) {
      console.error('Search failed:', error);
      setSearchResults([]);
      setShowSearchResults(true);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchInputChange = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const value = e.target.value;
    setSearchQuery(value);
    
    // Get suggestions if query is at least 2 characters
    if (value.trim().length >= 2) {
      try {
        const suggestions = await ApiService.getSearchSuggestions(value);
        setSearchSuggestions(suggestions);
        setShowSuggestions(suggestions.length > 0);
      } catch (error) {
        console.error('Failed to get search suggestions:', error);
        setShowSuggestions(false);
      }
    } else {
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestion: string): void => {
    setSearchQuery(suggestion);
    setShowSuggestions(false);
    // Trigger search automatically
    handleSearch();
  };

  const clearSearch = (): void => {
    setSearchQuery('');
    setSearchResults([]);
    setShowSearchResults(false);
    setShowSuggestions(false);
  };

  const handleAuthToggle = (): void => {
    setIsLoggedIn(!isLoggedIn);
    console.log(isLoggedIn ? 'User logged out' : 'User logged in');
  };

  const handleNewsletterSubscription = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    
    if (!newsletterEmail.trim()) {
      setNewsletterMessage('Please enter your email address');
      setNewsletterSuccess(false);
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newsletterEmail)) {
      setNewsletterMessage('Please enter a valid email address');
      setNewsletterSuccess(false);
      return;
    }

    setNewsletterLoading(true);
    setNewsletterMessage('');
    
    try {
      const result = await subscribeToNewsletter(newsletterEmail);
      
      if (result.success) {
        setNewsletterSuccess(true);
        setNewsletterMessage(result.message);
        setNewsletterEmail(''); // Clear the form
      } else {
        setNewsletterSuccess(false);
        setNewsletterMessage(result.message || 'Subscription failed. Please try again.');
      }
    } catch (error) {
      setNewsletterSuccess(false);
      setNewsletterMessage('An error occurred. Please try again later.');
      console.error('Newsletter subscription error:', error);
    } finally {
      setNewsletterLoading(false);
    }
  };

  const getDifficultyClass = (difficulty: Video['difficulty']): string => {
    switch (difficulty) {
      case 'beginner':
        return 'beginner';
      case 'intermediate':
        return 'intermediate';
      case 'advanced':
        return 'advanced';
      default:
        return 'beginner';
    }
  };

  const renderPageContent = () => {
    switch (currentPage) {
      case 'about':
        return (
          <div className="about-page">
            <div className="about-hero">
              <div className="about-content">
                <div className="about-image">
                  <img src="/clemente.jpg" alt="Clement Nkamanyi" className="profile-image" />
                  <h3 className="profile-name">Dipl. Ing. Clement Nkamanyi</h3>
                  <img src="/certifications1.png" alt="Professional Certifications" className="certifications-image" />
                </div>
                <div className="about-text">
                  <h1>Hello, My name is Clement Nkamanyi</h1>
                  <p className="about-intro">
                    I love technology. I am a Full stack Software Engineer with over 20 years of experience. 
                    I have helped fortune 500 companies like Daimler, Deutsche Telekom, Schindler, Julius Baer 
                    and Daimler Truck in designing and implementing scalable enterprise solutions.
                  </p>
                  
                  <div className="about-sections">
                    <div className="about-section">
                      <h2>🚀 My Mission</h2>
                      <p>
                        I also love sharing my knowledge even with kids. I mostly participate in events training 
                        kids how to code. I want to use this channel to continue to share that knowledge.
                      </p>
                    </div>
                    
                    <div className="about-section">
                      <h2>🎓 Education</h2>
                      <p>
                        I studied Computer Sciences and Communication Engineering at the University of Duisburg-Essen.
                        I also started a part-time MBA in Industrial Economics at the Blekinge University of Technology, 
                        which I hope to finish when time permits.
                      </p>
                    </div>
                    
                    <div className="about-section">
                      <h2>💝 Personal Life</h2>
                      <p>
                        I love coding and making things work. When not coding, I am either pushing my boys at their 
                        football games or serving my community as their leader. I have led numerous Cameroon Diaspora 
                        organizations.
                      </p>
                    </div>
                  </div>
                  
                  <div className="about-cta">
                    <button 
                      className="cta-button"
                      onClick={() => setCurrentPage('home')}
                    >
                      Explore My Content
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      case 'blog':
        // Show blog post detail if one is selected
        if (selectedBlogPostId) {
          return (
            <BlogPostDetail 
              postId={selectedBlogPostId} 
              onBack={() => setSelectedBlogPostId(null)}
            />
          );
        }
        
        // Otherwise show blog list
        return (
          <div className="blog-page">
            <div className="blog-hero">
              <div className="blog-content">
                <div className="blog-header">
                  <h1>Tech Insights & Stories</h1>
                  <p className="blog-intro">
                    I write to share the lessons learned from 20+ years in software engineering. Through my blog posts, 
                    I aim to simplify complex technical concepts, share real-world experiences from Fortune 500 projects, 
                    and help fellow developers navigate the ever-evolving tech landscape.
                  </p>
                  <p className="blog-mission">
                    Whether you're a beginner looking to understand fundamental concepts or a seasoned professional 
                    seeking insights on architecture decisions, my blog covers practical solutions, best practices, 
                    and the stories behind the code.
                  </p>
                </div>
                
                <div className="blog-posts-section">
                  <h2>Latest Articles</h2>
                  {blogLoading ? (
                    <div className="loading-message">Loading blog posts...</div>
                  ) : (
                    <div className="blog-posts-grid">
                      {blogPosts.length > 0 ? (
                        blogPosts.map((post) => (
                          <div key={post.id} className="blog-post-card">
                            <div className="blog-post-image">
                              <img 
                                src={post.imageUrl || '/blog-images/default-blog.jpg'} 
                                alt={post.imageAltText || post.title}
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.src = '/blog-images/default-blog.jpg';
                                }}
                              />
                            </div>
                            <div className="blog-post-content">
                              <h3>{post.title}</h3>
                              <p>{post.excerpt}</p>
                              <div className="blog-post-meta">
                                <span className="blog-post-date">
                                  {new Date(post.publishedAt).toLocaleDateString()}
                                </span>
                                {post.category && (
                                  <span className="blog-post-category">{post.category}</span>
                                )}
                              </div>
                              <button 
                                onClick={() => setSelectedBlogPostId(post.id)}
                                className="blog-post-link"
                              >
                                Read Article →
                              </button>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="no-blog-posts">
                          <p>No blog posts available at the moment. Check back soon!</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                <div className="blog-cta">
                  <div className="cta-content">
                    <h2>Want to Stay Updated?</h2>
                    <p>Get notified when I publish new articles and insights</p>
                    <div className="cta-buttons">
                      <button 
                        className="cta-button primary"
                        onClick={() => setCurrentPage('home')}
                      >
                        Subscribe to Newsletter
                      </button>
                      <button 
                        className="cta-button secondary"
                        onClick={() => setCurrentPage('about')}
                      >
                        Learn About My Journey
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      case 'newsletter':
        return (
          <div className="newsletter-page">
            <div className="newsletter-hero">
              <div className="newsletter-content">
                <div className="newsletter-header">
                  <h1>Stay Updated with Tech Insights</h1>
                  <p className="newsletter-intro">
                    Join hundreds of developers, architects, tech leaders and enthusiasts who receive monthly insights
                    on software engineering, cloud architecture, and industry best practices. Get exclusive 
                    content, early access to new videos, and practical tips delivered straight to your inbox.
                  </p>
                </div>

                <div className="newsletter-benefits">
                  <h2>What You'll Get</h2>
                  <div className="benefits-grid">
                    <div className="benefit-card">
                      <div className="benefit-icon">📧</div>
                      <h3>Monthly Insights</h3>
                      <p>Curated content on software engineering, cloud architecture, and emerging technologies.</p>
                    </div>
                    <div className="benefit-card">
                      <div className="benefit-icon">🎥</div>
                      <h3>Early Access</h3>
                      <p>Be the first to know about new videos, tutorials, and exclusive content.</p>
                    </div>
                    <div className="benefit-card">
                      <div className="benefit-icon">💡</div>
                      <h3>Expert Tips</h3>
                      <p>Practical advice from 20+ years of enterprise software development experience.</p>
                    </div>
                    <div className="benefit-card">
                      <div className="benefit-icon">🏗️</div>
                      <h3>Architecture Patterns</h3>
                      <p>Real-world examples and templates from Fortune 500 implementations.</p>
                    </div>
                    <div className="benefit-card">
                      <div className="benefit-icon">🚀</div>
                      <h3>Career Growth</h3>
                      <p>Insights on advancing your career in technology and leadership.</p>
                    </div>
                    <div className="benefit-card">
                      <div className="benefit-icon">🎯</div>
                      <h3>No Spam</h3>
                      <p>Quality over quantity. Unsubscribe anytime with one click.</p>
                    </div>
                  </div>
                </div>

                <div className="newsletter-signup-section">
                  <div className="signup-container">
                    <h2>Join the Community</h2>
                    <p>Subscribe now and get a free architecture patterns cheat sheet!</p>
                    
                    <form className="newsletter-form" onSubmit={handleNewsletterSubscription}>
                      <div className="form-group">
                        <input
                          type="email"
                          placeholder="Enter your email address"
                          value={newsletterEmail}
                          onChange={(e) => setNewsletterEmail(e.target.value)}
                          required
                          className="newsletter-input"
                        />
                        <button 
                          type="submit" 
                          disabled={newsletterLoading}
                          className="newsletter-submit-btn"
                        >
                          {newsletterLoading ? 'Subscribing...' : 'Subscribe Free'}
                        </button>
                      </div>
                      {newsletterMessage && (
                        <div className={`newsletter-message ${newsletterSuccess ? 'success' : 'error'}`}>
                          {newsletterMessage}
                        </div>
                      )}
                    </form>

                    <div className="newsletter-privacy">
                      <p>
                        🔒 Your privacy matters. We'll never share your email address or send spam. 
                        You can unsubscribe at any time.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="newsletter-testimonials">
                  <h2>What Subscribers Say</h2>
                  <div className="testimonials-grid">
                    <div className="testimonial-card">
                      <div className="testimonial-content">
                        <p>"Clement's insights have helped me improve my architecture skills significantly. The practical examples are invaluable."</p>
                      </div>
                      <div className="testimonial-author">
                        <strong>Sarah M.</strong>
                        <span>Senior Software Engineer</span>
                      </div>
                    </div>
                    <div className="testimonial-card">
                      <div className="testimonial-content">
                        <p>"The monthly newsletter is the highlight of my inbox. Always practical, always relevant to my work."</p>
                      </div>
                      <div className="testimonial-author">
                        <strong>David L.</strong>
                        <span>Solutions Architect</span>
                      </div>
                    </div>
                    <div className="testimonial-card">
                      <div className="testimonial-content">
                        <p>"I've implemented several patterns from Clement's content in my projects. Game changer!"</p>
                      </div>
                      <div className="testimonial-author">
                        <strong>Maria K.</strong>
                        <span>Enthusiast</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="newsletter-cta">
                  <div className="cta-content">
                    <h2>Ready to Level Up Your Tech Skills?</h2>
                    <p>Join the community and start receiving valuable insights today!</p>
                    <div className="cta-buttons">
                      <button 
                        className="cta-button"
                        onClick={() => {
                          const signupSection = document.querySelector('.newsletter-signup-section');
                          if (signupSection) {
                            signupSection.scrollIntoView({ behavior: 'smooth' });
                          }
                        }}
                      >
                        Subscribe Now
                      </button>
                      <button 
                        className="cta-button secondary"
                        onClick={() => setCurrentPage('about')}
                      >
                        Learn About Clement
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      case 'videos':
        return (
          <div className="videos-page">
            <div className="videos-hero">
              <div className="videos-content">
                <div className="videos-header">
                  <h1>Latest Videos</h1>
                  <p className="videos-intro">
                    Explore my latest tech tutorials, deep dives, and insights. From beginner-friendly explanations 
                    to advanced enterprise solutions, find the content that matches your learning journey.
                  </p>
                </div>
                
                <div className="videos-search-section">
                  <div className="search-box">
                    <input 
                      type="text" 
                      placeholder="Search videos by topic, technology, or keyword..."
                      value={searchQuery}
                      onChange={handleSearchInputChange}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    />
                    <button onClick={handleSearch} className="search-button">
                      🔍 Search
                    </button>
                    {isSearching && <div className="search-loading">Searching...</div>}
                    {showSuggestions && searchSuggestions.length > 0 && (
                      <div className="search-suggestions">
                        {searchSuggestions.map((suggestion) => (
                          <div 
                            key={suggestion} 
                            className="suggestion-item"
                            onClick={() => handleSuggestionClick(suggestion)}
                          >
                            {suggestion}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {showSearchResults && (
                    <button onClick={clearSearch} className="clear-search-btn">
                      ✕ Clear Search
                    </button>
                  )}
                </div>

                {/* Search Results */}
                {showSearchResults && (
                  <div className="videos-search-results">
                    <h2>Search Results for "{searchQuery}"</h2>
                    {searchResults.length === 0 ? (
                      <div className="no-results">
                        <p>No videos found for "{searchQuery}". Try different keywords or check the spelling.</p>
                      </div>
                    ) : (
                      <div className="videos-grid">
                        {searchResults.filter(result => result.type === 'video').map((result) => (
                          <div 
                            key={`search-${result.id}`} 
                            className="video-card"
                            onClick={() => result.videoUrl && window.open(result.videoUrl, '_blank')}
                          >
                            <div className="video-thumbnail">
                              {result.thumbnailUrl ? (
                                <img 
                                  src={result.thumbnailUrl} 
                                  alt={result.title}
                                  className="video-thumbnail-image"
                                />
                              ) : (
                                <div className="video-thumbnail-fallback">
                                  🎥
                                </div>
                              )}
                              <div className="video-play-overlay">▶️</div>
                            </div>
                            <div className="video-info">
                              <h3 className="video-title">{result.title}</h3>
                              <p className="video-description">{result.description}</p>
                              {result.tags && (
                                <div className="video-tags">
                                  {result.tags.split(',').slice(0, 3).map((tag) => (
                                    <span key={`${result.id}-${tag.trim()}`} className="tag">{tag.trim()}</span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Latest Videos Grid */}
                {!showSearchResults && (
                  <div className="latest-videos-section">
                    <div className="section-header">
                      <h2>Recent Uploads</h2>
                      <button 
                        onClick={fetchVideos} 
                        disabled={loading}
                        className="refresh-button"
                      >
                        {loading ? 'Refreshing...' : '🔄 Refresh'}
                      </button>
                    </div>
                    
                    {error && (
                      <div className="error-message">
                        {error}
                      </div>
                    )}
                    
                    {loading ? (
                      <div className="loading-message">
                        Loading videos...
                      </div>
                    ) : (
                      <div className="videos-grid">
                        {videos.length === 0 && !error ? (
                          <div className="no-videos">
                            No videos available at the moment.
                          </div>
                        ) : (
                          videos.slice(0, 6).map((video: Video) => (
                            <div 
                              key={video.id} 
                              className="video-card"
                              onClick={() => video.videoUrl && window.open(video.videoUrl, '_blank')}
                            >
                              <div className="video-thumbnail">
                                {video.thumbnailUrl ? (
                                  <img 
                                    src={video.thumbnailUrl} 
                                    alt={video.title}
                                    className="video-thumbnail-image"
                                    onError={(e) => {
                                      e.currentTarget.style.display = 'none';
                                      const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                                      if (fallback) fallback.style.display = 'block';
                                    }}
                                  />
                                ) : null}
                                <div 
                                  className="video-thumbnail-fallback" 
                                  style={{ display: video.thumbnailUrl ? 'none' : 'block' }}
                                >
                                  {video.icon}
                                </div>
                                <div className="video-play-overlay">▶️</div>
                                {video.duration && (
                                  <div className="video-duration-badge">
                                    {video.duration}
                                  </div>
                                )}
                              </div>
                              <div className="video-info">
                                <h3 className="video-title">{video.title}</h3>
                                <p className="video-description">{video.description}</p>
                                <span className={`difficulty-badge ${getDifficultyClass(video.difficulty)}`}>
                                  {video.difficulty}
                                </span>
                                {video.views && (
                                  <div className="video-stats">
                                    👁 {video.views.toLocaleString()} views
                                    {video.likes && ` • 👍 ${video.likes.toLocaleString()}`}
                                    {video.publishedAt && ` • 📅 ${new Date(video.publishedAt).toLocaleDateString()}`}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                )}
                
                <div className="videos-cta">
                  <div className="cta-content">
                    <h2>Want More Content?</h2>
                    <p>Subscribe to my YouTube channel for the latest tech tutorials and insights</p>
                    <div className="cta-buttons">
                      <a 
                        href="https://youtube.com/@AskClement" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="cta-button primary"
                      >
                        🎥 Subscribe on YouTube
                      </a>
                      <button 
                        className="cta-button secondary"
                        onClick={() => setCurrentPage('home')}
                      >
                        📧 Get Newsletter Updates
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      case 'courses':
        return (
          <div className="courses-page">
            <div className="courses-hero">
              <div className="courses-content">
                <div className="courses-header">
                  <div className="courses-text">
                    <h1>Training & Courses</h1>
                    <p className="courses-intro">
                      Transform your team's capabilities with expert-led training in AI, Cloud, and Software Development. 
                      With 20+ years building enterprise solutions for Fortune 500 companies, I bring real-world experience 
                      to every training session.
                    </p>
                    <p className="courses-mission">
                      From introducing children to coding fundamentals to advanced AI and cloud architecture for enterprise teams, 
                      I offer tailored training programs that deliver practical skills your organization can use immediately.
                    </p>
                  </div>
                  <div className="courses-image">
                    <img src="/courses.jpg" alt="Training and Courses" className="courses-hero-image" />
                  </div>
                </div>

                <div className="training-formats">
                  <h2>🎯 Flexible Training Options</h2>
                  <div className="benefits-grid">
                    <div className="benefit-card">
                      <div className="benefit-icon">🏢</div>
                      <h3>Onsite Training</h3>
                      <p>Bring expert training directly to your team's workspace for maximum engagement and collaboration.</p>
                      <ul>
                        <li>✓ Customized to your specific tech stack</li>
                        <li>✓ Hands-on workshops with your data</li>
                        <li>✓ Team building through shared learning</li>
                        <li>✓ Immediate application to current projects</li>
                      </ul>
                    </div>
                    <div className="benefit-card">
                      <div className="benefit-icon">💻</div>
                      <h3>Remote Training</h3>
                      <p>Interactive online sessions that deliver the same quality as in-person training with global accessibility.</p>
                      <ul>
                        <li>✓ Live, interactive virtual sessions</li>
                        <li>✓ Global team participation</li>
                        <li>✓ Recorded sessions for later review</li>
                        <li>✓ Digital resources and materials</li>
                      </ul>
                    </div>
                    <div className="benefit-card">
                      <div className="benefit-icon">🔄</div>
                      <h3>Hybrid Training</h3>
                      <p>Combine the best of both worlds with flexible delivery that adapts to your team's needs.</p>
                      <ul>
                        <li>✓ Mix of onsite and remote sessions</li>
                        <li>✓ Flexible scheduling options</li>
                        <li>✓ Accommodates different learning styles</li>
                        <li>✓ Cost-effective solution</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="courses-section">
                  <h2>🎓 Training Specializations</h2>
                  <div className="benefits-grid">
                    <div className="benefit-card">
                      <div className="benefit-icon">🤖</div>
                      <h3>AI & Machine Learning</h3>
                      <p>From fundamentals to advanced implementations, learn to build AI solutions that drive business value.</p>
                      <ul className="course-highlights">
                        <li>✓ AI Fundamentals & Strategy</li>
                        <li>✓ Machine Learning Operations</li>
                        <li>✓ Large Language Models</li>
                        <li>✓ Computer Vision & NLP</li>
                      </ul>
                      <div className="course-footer">
                        <button 
                          className="course-enquire-btn"
                          onClick={() => {
                            alert('AI Training enquiry - Coming soon! Use Ask Question feature for now.');
                          }}
                        >
                          Get AI Training Quote
                        </button>
                      </div>
                    </div>
                    
                    <div className="benefit-card">
                      <div className="benefit-icon">☁️</div>
                      <h3>Cloud Architecture</h3>
                      <p>Master modern cloud platforms with hands-on training in Azure, AWS, and cloud-native development practices.</p>
                      <ul className="course-highlights">
                        <li>✓ Azure & AWS Fundamentals</li>
                        <li>✓ Microservices Architecture</li>
                        <li>✓ DevOps & CI/CD Pipelines</li>
                        <li>✓ Kubernetes & Containerization</li>
                      </ul>
                      <div className="course-footer">
                        <button 
                          className="course-enquire-btn"
                          onClick={() => {
                            alert('Cloud Training enquiry - Coming soon! Use Ask Question feature for now.');
                          }}
                        >
                          Get Cloud Training Quote
                        </button>
                      </div>
                    </div>
                    
                    <div className="benefit-card">
                      <div className="benefit-icon">👶</div>
                      <h3>Kids Coding</h3>
                      <p>Introduce young minds to programming with age-appropriate courses that make coding fun and engaging.</p>
                      <ul className="course-highlights">
                        <li>✓ Scratch Programming (Ages 6-12)</li>
                        <li>✓ Python for Beginners (Ages 10+)</li>
                        <li>✓ Web Development for Teens</li>
                        <li>✓ Game Development Basics</li>
                      </ul>
                      <div className="course-footer">
                        <button 
                          className="course-enquire-btn"
                          onClick={() => {
                            alert('Kids Coding enquiry - Coming soon! Use Ask Question feature for now.');
                          }}
                        >
                          Get Kids Training Quote
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="available-courses-section">
                  <h2>📚 Available Courses</h2>
                  {coursesLoading ? (
                    <div className="loading-message">Loading courses...</div>
                  ) : (
                    <div className="courses-grid">
                      {courses.length > 0 ? (
                        courses.map((course) => (
                          <div key={course.id} className="course-card">
                            <div className="course-header">
                              <div className="course-icon">{course.icon}</div>
                              <div className="course-meta">
                                <span className="course-category">{course.category}</span>
                                <span className="course-level">{course.level}</span>
                              </div>
                            </div>
                            <h3>{course.title}</h3>
                            <p className="course-description">{course.shortDescription}</p>
                            <div className="course-details">
                              <div className="course-duration">
                                <span className="detail-icon">⏱️</span>
                                {course.duration}
                              </div>
                              <div className="course-format">
                                <span className="detail-icon">📍</span>
                                {course.format}
                              </div>
                              <div className="course-audience">
                                <span className="detail-icon">👥</span>
                                {course.targetAudience}
                              </div>
                            </div>
                            <div className="course-highlights">
                              {course.highlights && course.highlights.slice(0, 3).map((highlight: string, index: number) => (
                                <div key={index} className="highlight-item">
                                  ✓ {highlight}
                                </div>
                              ))}
                            </div>
                            <div className="course-footer">
                              <div className="course-price">
                                {course.price}
                              </div>
                              {course.isAvailable ? (
                                <button 
                                  className="course-enquire-btn"
                                  onClick={() => {
                                    // TODO: Open course enquiry modal
                                    alert(`Course enquiry for ${course.title} - Coming soon!`);
                                  }}
                                >
                                  Get Quote
                                </button>
                              ) : (
                                <button className="course-coming-soon-btn">Coming Soon</button>
                              )}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="no-courses">
                          <p>New courses are being prepared. Check back soon!</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="courses-cta">
                  <div className="cta-content">
                    <h2>Ready to Upskill Your Team?</h2>
                    <p>Contact me to discuss your training needs and get a customized program proposal</p>
                    <div className="cta-buttons">
                      <button 
                        className="cta-button primary"
                        onClick={() => {
                          // TODO: Open contact modal or scroll to contact section
                          alert('Contact form coming soon! For now, please use the Ask Question feature.');
                        }}
                      >
                        Get Custom Training Quote
                      </button>
                      <button 
                        className="cta-button secondary"
                        onClick={() => setCurrentPage('about')}
                      >
                        Learn About My Experience
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      case 'templates':
        return (
          <div className="templates-page">
            <div className="templates-hero">
              <div className="templates-content">
                <div className="templates-header">
                  <div className="templates-text">
                    <h1>Architecture Templates</h1>
                    <p className="templates-intro">
                      Ever wondered if you could profit from some code structure that works? You do not need to 
                      re-invent the wheels. I have worked on many software projects - monoliths, modular monoliths, 
                      microservices. There is always a design decision and tradeoff for each.
                    </p>
                    <p className="templates-mission">
                      On this page I shall be sharing proven architecture patterns, best practices, and 
                      battle-tested templates from my 20+ years building enterprise solutions.
                    </p>
                  </div>
                  <div className="templates-image">
                    <img src="/futuristic-architecture.jpg" alt="Software Architecture Templates" className="template-hero-image" />
                  </div>
                </div>
                
                <div className="coming-up-section">
                  <div className="section-header">
                    <h2>🚀 Architecture Templates</h2>
                    {isAdmin && (
                      <button 
                        className="admin-create-btn"
                        onClick={() => setShowCreateTemplateModal(true)}
                      >
                        + Create Template
                      </button>
                    )}
                  </div>
                  {templatesLoading ? (
                    <div className="loading-message">Loading templates...</div>
                  ) : (
                    <div className="template-categories">
                      {archTemplates.length > 0 ? (
                        archTemplates.map((template) => (
                          <div key={template.rowKey} className="template-category">
                            <div className="category-icon">{template.icon}</div>
                            <h3>{template.title}</h3>
                            <p>{template.description}</p>
                            <ul dangerouslySetInnerHTML={{ __html: template.highlights }} />
                            {template.isAvailable ? (
                              <button 
                                className="download-available-badge"
                                onClick={() => handleTemplateButtonClick(template)}
                              >
                                Get Template
                              </button>
                            ) : (
                              <button className="coming-up-badge">Coming Up</button>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="no-templates">
                          <p>No templates available at the moment. Check back soon!</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                <div className="templates-cta">
                  <div className="cta-content">
                    <h2>Ready to Build Better Software?</h2>
                    <p>Get notified when new templates are available</p>
                    <div className="cta-buttons">
                      <button 
                        className="cta-button primary"
                        onClick={() => setCurrentPage('home')}
                      >
                        Subscribe to Updates
                      </button>
                      <button 
                        className="cta-button secondary"
                        onClick={() => setCurrentPage('about')}
                      >
                        Learn About My Experience
                      </button>
                    </div>
                  </div>
                </div>

                <CreateTemplateModal
                  isOpen={showCreateTemplateModal}
                  onClose={() => setShowCreateTemplateModal(false)}
                  onTemplateCreated={handleTemplateCreated}
                  userEmail={userEmail || undefined}
                />
              </div>
            </div>
          </div>
        );
      case 'create-blog':
        return (
          <ProtectedRoute requireAdmin={true}>
            <CreateBlogPost />
          </ProtectedRoute>
        );
      case 'manage-posts':
        return (
          <ProtectedRoute requireAdmin={true}>
            <ManagePosts onPageChange={handlePageChange} />
          </ProtectedRoute>
        );
      default:
        return (
          <>
            <section className="hero">
              <div className="container hero-content">
                <h1>Ask Clement</h1>
                <p className="tagline">Tech Made Simple</p>
                <div className="search-box">
                  <input 
                    type="text" 
                    placeholder="Search for any tech topic..."
                    value={searchQuery}
                    onChange={handleSearchInputChange}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  />
                  {isSearching && <div className="search-loading">Searching...</div>}
                  {showSuggestions && searchSuggestions.length > 0 && (
                    <div className="search-suggestions">
                      {searchSuggestions.map((suggestion) => (
                        <div 
                          key={suggestion} 
                          className="suggestion-item"
                          onClick={() => handleSuggestionClick(suggestion)}
                        >
                          {suggestion}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <p>From beginner-friendly explanations to deep technical dives</p>
              </div>
            </section>

            <main className="main-content">
              <div className="container">
                {/* Search Results Section */}
                {showSearchResults && (
                  <section className="section search-results">
                    <div className="search-results-header">
                      <h2>Search Results for "{searchQuery}"</h2>
                      <button onClick={clearSearch} className="clear-search-btn">
                        ✕ Clear Search
                      </button>
                    </div>
                    {searchResults.length === 0 ? (
                      <div className="no-results">
                        <p>No results found for "{searchQuery}". Try different keywords or check the spelling.</p>
                      </div>
                    ) : (
                      <div className="search-results-grid">
                        {searchResults.map((result) => (
                          <div 
                            key={`${result.type}-${result.id}`} 
                            className={`search-result-card ${result.type}`}
                            onClick={() => result.videoUrl && window.open(result.videoUrl, '_blank')}
                            style={{ cursor: result.videoUrl ? 'pointer' : 'default' }}
                          >
                            <div className="result-type-badge">
                              {result.type === 'video' ? '🎥' : '❓'} {result.type}
                            </div>
                            {result.type === 'video' && result.thumbnailUrl && (
                              <div className="search-result-thumbnail">
                                <img 
                                  src={result.thumbnailUrl} 
                                  alt={result.title}
                                  className="search-result-thumbnail-image"
                                />
                              </div>
                            )}
                            <div className="search-result-content">
                              <h3 className="result-title">{result.title}</h3>
                              {result.description && (
                                <p className="result-description">{result.description}</p>
                              )}
                              {result.tags && (
                                <div className="result-tags">
                                  {result.tags.split(',').map((tag) => (
                                    <span key={`${result.type}-${result.id}-${tag.trim()}`} className="tag">{tag.trim()}</span>
                                  ))}
                                </div>
                              )}
                              {result.videoUrl && (
                                <a href={result.videoUrl} target="_blank" rel="noopener noreferrer" className="video-link">
                                  Watch Video
                                </a>
                              )}
                              <div className="result-meta">
                                <span className="relevance-score">
                                  Relevance: {Math.round(result.relevanceScore)}%
                                </span>
                                <span className="created-date">
                                  {new Date(result.createdAt).toLocaleDateString()}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </section>
                )}
                
                <section className="section">
                  <h2>Latest Videos</h2>
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '2rem' }}>
                    <button 
                      onClick={fetchVideos} 
                      disabled={loading}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: loading ? '#666' : '#00a8cc',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        transition: 'all 0.3s ease',
                        fontWeight: '500'
                      }}
                    >
                      {loading ? 'Refreshing...' : '🔄 Refresh'}
                    </button>
                  </div>
                  {error && (
                    <div className="error-message" style={{ 
                      color: '#e74c3c', 
                      textAlign: 'center', 
                      padding: '1rem',
                      backgroundColor: '#ffebee',
                      borderRadius: '4px',
                      marginBottom: '1rem'
                    }}>
                      {error}
                    </div>
                  )}
                  {loading ? (
                    <div className="loading-message" style={{
                      textAlign: 'center',
                      padding: '2rem',
                      color: '#666'
                    }}>
                      Loading videos...
                    </div>
                  ) : (
                    <div className="video-grid" id="video-grid">
                      {videos.length === 0 && !error ? (
                        <div style={{ 
                          textAlign: 'center', 
                          padding: '2rem',
                          color: '#666'
                        }}>
                          No videos available at the moment.
                        </div>
                      ) : (
                        videos.map((video: Video) => (
                          <div 
                            key={video.id} 
                            className="video-card"
                            onClick={() => video.videoUrl && window.open(video.videoUrl, '_blank')}
                            style={{ cursor: video.videoUrl ? 'pointer' : 'default' }}
                          >
                            <div className="video-thumbnail">
                              {video.thumbnailUrl ? (
                                <img 
                                  src={video.thumbnailUrl} 
                                  alt={video.title}
                                  className="video-thumbnail-image"
                                  onError={(e) => {
                                    // Fallback to icon if image fails to load
                                    e.currentTarget.style.display = 'none';
                                    const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                                    if (fallback) fallback.style.display = 'block';
                                  }}
                                />
                              ) : null}
                              <div 
                                className="video-thumbnail-fallback" 
                                style={{ display: video.thumbnailUrl ? 'none' : 'block' }}
                              >
                                {video.icon}
                              </div>
                              {video.duration && (
                                <div className="video-duration-badge">
                                  {video.duration}
                                </div>
                              )}
                            </div>
                            <div className="video-info">
                              <h3 className="video-title">{video.title}</h3>
                              <p className="video-description">{video.description}</p>
                              <span className={`difficulty-badge ${getDifficultyClass(video.difficulty)}`}>
                                {video.difficulty}
                              </span>
                              {video.views && (
                                <div className="video-stats" style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.5rem' }}>
                                  👁 {video.views.toLocaleString()} views
                                  {video.likes && ` • 👍 ${video.likes.toLocaleString()}`}
                                  {video.publishedAt && ` • 📅 ${new Date(video.publishedAt).toLocaleDateString()}`}
                                </div>
                              )}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </section>

                <section className="section">
                  <h2>Popular Questions</h2>
                  <div className="questions-grid">
                    {loading ? (
                      // Loading state for questions
                      Array.from({ length: 4 }, (_, index) => (
                        <div key={`skeleton-${index}`} className="question-card loading">
                          <div className="skeleton skeleton-title"></div>
                          <div className="skeleton skeleton-text"></div>
                          <div className="skeleton skeleton-text"></div>
                        </div>
                      ))
                    ) : questions.length > 0 ? (
                      questions.map((q: Question) => (
                        <div key={q.id} className="question-card">
                          <h3>{q.title}</h3>
                          <p>{q.content}</p>
                          <div className="question-meta">
                            <span className="category">{q.category}</span>
                            <span className="views">{q.views} views</span>
                            <span className="likes">{q.likes} likes</span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="no-questions">
                        <p>No popular questions available at the moment.</p>
                      </div>
                    )}
                  </div>
                </section>
              </div>
            </main>

            <section className="newsletter-section" id="newsletter">
              <div className="container">
                <h2>Stay Updated</h2>
                <p>Get the latest tech insights, tutorials, and tips delivered straight to your inbox</p>
                
                {newsletterMessage && (
                  <div className={`newsletter-message ${newsletterSuccess ? 'success' : 'error'}`}>
                    {newsletterMessage}
                  </div>
                )}
                
                <form className="newsletter-form" onSubmit={handleNewsletterSubscription}>
                  <input 
                    type="email" 
                    placeholder="Enter your email address"
                    className="newsletter-input"
                    value={newsletterEmail}
                    onChange={(e) => setNewsletterEmail(e.target.value)}
                    disabled={newsletterLoading}
                  />
                  <button 
                    type="submit" 
                    className="newsletter-button"
                    disabled={newsletterLoading}
                  >
                    {newsletterLoading ? 'Subscribing...' : 'Subscribe'}
                  </button>
                </form>
                
                <p className="newsletter-note">
                  📧 Weekly newsletter • 🚫 No spam • 🔓 Unsubscribe anytime
                </p>
              </div>
            </section>

            <section className="ask-section" id="ask">
              <div className="container">
                <h2>Got a Tech Question?</h2>
                <p>Ask me anything about technology - I'll explain it in a way that makes sense!</p>
                <button 
                  className="cta-button"
                  onClick={() => setShowQuestionForm(true)}
                >
                  Ask a Question
                </button>
                <div className="ask-section-logo">
                  <img src="/askClementLogo5.png" alt="Ask Clement" className="ask-section-logo-image" />
                </div>
              </div>
            </section>

            {showQuestionForm && (
              <QuestionSubmissionForm onClose={() => setShowQuestionForm(false)} />
            )}

            {showTemplateModal && selectedTemplate && (
              <TemplateRequestModal
                templateTitle={selectedTemplate.title}
                templateId={selectedTemplate.rowKey}
                onClose={() => {
                  setShowTemplateModal(false);
                  setSelectedTemplate(null);
                }}
                onRequest={handleTemplateRequest}
              />
            )}
          </>
        );
    }
  };

  return (
    <div className="app">
      <header className="header">
      <nav className="nav container">
        <a href="/" className="logo">
          <img src="/askclemlogo6.png" alt="Ask Clement" className="logo-image" />
        </a>
          
          {/* Desktop Navigation */}
          <ul className={`nav-links ${isMobileMenuOpen ? 'nav-links-mobile-open' : ''}`}>
            <li><button onClick={() => handlePageChange('home')} className={currentPage === 'home' ? 'active' : ''}>Home</button></li>
            <li><button onClick={() => handlePageChange('videos')} className={currentPage === 'videos' ? 'active' : ''}>Videos</button></li>
            <li><button onClick={() => handlePageChange('blog')} className={currentPage === 'blog' ? 'active' : ''}>Blog</button></li>
            <li><button onClick={() => handlePageChange('courses')} className={currentPage === 'courses' ? 'active' : ''}>Courses</button></li>
            <li><button onClick={() => handlePageChange('newsletter')} className={currentPage === 'newsletter' ? 'active' : ''}>Newsletter</button></li>
            <li><button onClick={() => handlePageChange('templates')} className={currentPage === 'templates' ? 'active' : ''}>Templates</button></li>
            {isAdmin && (
              <li><button onClick={() => handlePageChange('create-blog')} className={currentPage === 'create-blog' ? 'active' : ''}>Create Blog</button></li>
            )}
            <li><button onClick={() => handlePageChange('about')} className={currentPage === 'about' ? 'active' : ''}>About</button></li>
          </ul>
          
          <div className={`nav-actions ${isMobileMenuOpen ? 'nav-actions-mobile-open' : ''}`}>
            <button onClick={handleAskQuestionClick} className="cta-button">Ask Question</button>
            <UserMenu onPageChange={handlePageChange} />
          </div>
          
          {/* Mobile Hamburger Menu */}
          <button 
            className={`mobile-menu-toggle ${isMobileMenuOpen ? 'mobile-menu-toggle-open' : ''}`}
            onClick={toggleMobileMenu}
            aria-label="Toggle mobile menu"
          >
            <span className="hamburger-line"></span>
            <span className="hamburger-line"></span>
            <span className="hamburger-line"></span>
          </button>
        </nav>
        
        {/* Mobile Menu Overlay */}
        {isMobileMenuOpen && (
          <div className="mobile-menu-overlay" onClick={() => setIsMobileMenuOpen(false)}>
            <div className="mobile-menu-content" onClick={(e) => e.stopPropagation()}>
              <div className="mobile-menu-header">
                <img src="/askclemlogo6.png" alt="Ask Clement" className="mobile-menu-logo" />
                <button 
                  className="mobile-menu-close"
                  onClick={() => setIsMobileMenuOpen(false)}
                  aria-label="Close mobile menu"
                >
                  ×
                </button>
              </div>
              <ul className="mobile-menu-links">
                <li><button onClick={() => handlePageChange('home')} className={currentPage === 'home' ? 'active' : ''}>Home</button></li>
                <li><button onClick={() => handlePageChange('videos')} className={currentPage === 'videos' ? 'active' : ''}>Videos</button></li>
                <li><button onClick={() => handlePageChange('blog')} className={currentPage === 'blog' ? 'active' : ''}>Blog</button></li>
                <li><button onClick={() => handlePageChange('courses')} className={currentPage === 'courses' ? 'active' : ''}>Courses</button></li>
                <li><button onClick={() => handlePageChange('newsletter')} className={currentPage === 'newsletter' ? 'active' : ''}>Newsletter</button></li>
                <li><button onClick={() => handlePageChange('templates')} className={currentPage === 'templates' ? 'active' : ''}>Templates</button></li>
                {isAdmin && (
                  <li><button onClick={() => handlePageChange('create-blog')} className={currentPage === 'create-blog' ? 'active' : ''}>Create Blog</button></li>
                )}
                <li><button onClick={() => handlePageChange('about')} className={currentPage === 'about' ? 'active' : ''}>About</button></li>
              </ul>
              <div className="mobile-menu-actions">
                <button onClick={handleAskQuestionClick} className="mobile-cta-button">Ask Question</button>
                <button 
                  onClick={() => { handleAuthToggle(); setIsMobileMenuOpen(false); }} 
                  className={`mobile-auth-button ${isLoggedIn ? 'logout' : ''}`}
                >
                  {isLoggedIn ? 'Logout' : 'Login'}
                </button>
              </div>
            </div>
          </div>
        )}
      </header>

      {renderPageContent()}

      <footer className="footer">
        <div className="container">
          <div className="footer-links">
            <a href="https://youtube.com/@AskClement" target="_blank" rel="noopener noreferrer">YouTube</a>
            <a href="https://x.com/cnkamanyi" target="_blank" rel="noopener noreferrer">Twitter</a>
            <a href="https://www.linkedin.com/in/clement-nkamanyi" target="_blank" rel="noopener noreferrer">LinkedIn</a>
            <a href="https://medium.com/@cnkamanyi">Medium</a>
            <a href="https://www.clenkasoft.com/web/en/contact" target="_blank" rel="noopener noreferrer">Contact</a>
          </div>
          <p>&copy; 2025 Ask Clement. Making technology accessible to everyone.</p>
        </div>
      </footer>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
};

export default App;